import React, { useContext } from 'react';
import { Drawer as MDrawer } from '@material-ui/core';
import { observer } from 'mobx-react-lite';
import { GlobalsContext } from '../../store/Globals';
import {
  List,
  Container,
  CategoryDivider,
  DrawerHeader,
  DrawerFooter,
} from './styles';
import DrawerList from './DrawerList';
import TransItemList from './TransItemList';
import coreLogo from '../../assets/core.jpg';
import type { TransactionListElement } from '../../types/TransactionList';

type SetDrawerOpen = (open: boolean) => boolean;

const Drawer = observer(() => {
  const globals = useContext(GlobalsContext);
  const {
    assetList,
    definitions,
    isDrawerOpen,
    setDrawerOpen,
    transactionList,
    checkPermission,
    checkUnreachablePermission,
    standardTxList,
    dynamicTxsAllowed,
    hasUserPermission,
  } = globals;
  const { colors } = definitions;

  const dynamicTxs = standardTxList.filter(
    (tx) => tx.tag === 'createAssetType' || tx.tag === 'deleteAssetType',
  );

  const getAssetPermission = (assetTag: string): string | null => {
    const permissionMap: { [key: string]: string } = {
      'debt': 'readDebt',
      'organization': 'readOrganization',
    };
    return permissionMap[assetTag] || null;
  };

  const canViewAsset = (asset: any): boolean => {
    const requiredPermission = getAssetPermission(asset.tag);
    if (requiredPermission) {
      if (!hasUserPermission(requiredPermission)) {
        return false;
      }
    } else {
      return false;
    }
    return checkPermission(asset.readers);
  };

  const getTransactionPermission = (tag: string): string | null => {
    const permissionMap: { [key: string]: string } = {
      'createDebt': 'createDebt',
      'closeDebt': 'closeDebt',
      'createOrganization': 'writeOrganization',
      'revokeOrganization': 'writeOrganization',
      'initDefaultOrganization': 'writeOrganization',
      'createAssetType': 'writeOrganization',
      'updateAssetType': 'writeOrganization',
      'deleteAssetType': 'writeOrganization',
    };
    return permissionMap[tag] || null;
  };

  const canExecuteTransaction = (tx: any): boolean => {
    if (!checkPermission(tx.callers)) {
      return false;
    }
    
    const requiredPermission = getTransactionPermission(tx.tag);
    if (requiredPermission) {
      return hasUserPermission(requiredPermission);
    }
    
    return false;
  };

  return (
    <div>
      <MDrawer
        open={isDrawerOpen}
        onClose={() => setDrawerOpen(false)}
        variant="temporary"
      >
        <Container colors={colors}>
          <DrawerHeader>
            <img src={coreLogo} alt="Core logo" />
          </DrawerHeader>

          <List colors={colors}>
            <DrawerList
              tag=""
              label="Home"
              color={colors[2]}
              setDrawerOpen={setDrawerOpen as SetDrawerOpen}
            />

            <CategoryDivider />

            <DrawerList
              tag=""
              title
              disabled
              color={colors[2]}
              label="Assets (Read/Write)"
              setDrawerOpen={setDrawerOpen as SetDrawerOpen}
            />
            {assetList
              ? assetList.map((item) =>
                  item.canCreate && canViewAsset(item) ? (
                    <DrawerList
                      key={item.tag}
                      tag={item.tag}
                      color={colors[2]}
                      label={item.label}
                      readers={item.readers}
                      setDrawerOpen={setDrawerOpen as SetDrawerOpen}
                    />
                  ) : null,
                )
              : null}

            <CategoryDivider />

            <DrawerList
              tag=""
              title
              disabled
              color={colors[2]}
              label="Assets (Read Only)"
              setDrawerOpen={setDrawerOpen as SetDrawerOpen}
            />
            {assetList
              ? assetList.map((item) =>
                  !item.canCreate && canViewAsset(item) ? (
                    <DrawerList
                      key={item.tag}
                      tag={item.tag}
                      color={colors[2]}
                      label={item.label}
                      readers={item.readers}
                      setDrawerOpen={setDrawerOpen as SetDrawerOpen}
                    />
                  ) : null,
                )
              : null}

            <CategoryDivider />

            <DrawerList
              tag=""
              title
              disabled
              color={colors[2]}
              label="Assets (Unreachable)"
              setDrawerOpen={setDrawerOpen as SetDrawerOpen}
            />
            {assetList
              ? assetList.map((item) =>
                  checkUnreachablePermission(item.writers, item.readers) ? (
                    <DrawerList
                      key={item.tag}
                      tag={item.tag}
                      color={colors[2]}
                      label={item.label}
                      readers={item.readers}
                      setDrawerOpen={setDrawerOpen as SetDrawerOpen}
                    />
                  ) : null,
                )
              : null}

            <CategoryDivider />

            <DrawerList
              tag=""
              title
              disabled
              color={colors[2]}
              label="Active Transactions"
              setDrawerOpen={setDrawerOpen as SetDrawerOpen}
            />
            {transactionList
              ? transactionList.map((item) =>
                  canExecuteTransaction(item) ? (
                    item.tag.length > 0 ? (
                      <TransItemList
                        key={item.tag}
                        tag={item.tag}
                        color={colors[2]}
                        label={item.label}
                        setDrawerOpen={setDrawerOpen as SetDrawerOpen}
                      />
                    ) : null
                  ) : null,
                )
              : null}

            <CategoryDivider />

            <DrawerList
              tag=""
              title
              disabled
              color={colors[2]}
              label="Non-Callable Transactions"
              setDrawerOpen={setDrawerOpen as SetDrawerOpen}
            />
            {transactionList
              ? transactionList.map((item) =>
                  !canExecuteTransaction(item) ? (
                    item.tag.length > 0 ? (
                      <TransItemList
                        key={item.tag}
                        tag={item.tag}
                        color={colors[2]}
                        label={item.label}
                        setDrawerOpen={setDrawerOpen as SetDrawerOpen}
                      />
                    ) : null
                  ) : null,
                )
              : null}

            {dynamicTxs.length > 0 ? (
              dynamicTxsAllowed ? (
                <>
                  <CategoryDivider />
                  <DrawerList
                    tag=""
                    title
                    disabled
                    color={colors[2]}
                    label="Dynamic Type Transactions"
                    setDrawerOpen={setDrawerOpen as SetDrawerOpen}
                  />
                </>
              ) : null
            ) : null}
            {dynamicTxs
              ? dynamicTxs.map((item: TransactionListElement) =>
                  dynamicTxsAllowed && canExecuteTransaction(item) ? (
                    item.tag.length > 0 ? (
                      <TransItemList
                        key={item.tag}
                        tag={item.tag}
                        color={colors[2]}
                        label={item.label}
                        setDrawerOpen={setDrawerOpen as SetDrawerOpen}
                      />
                    ) : null
                  ) : null,
                )
              : null}
          </List>

          <DrawerFooter>
            Core Blockchain v{definitions.version}
          </DrawerFooter>
        </Container>
      </MDrawer>
    </div>
  );
});

export default Drawer;